import { useEffect, useLayoutEffect, useState } from "react";

export interface TourStep {
  /** Seletor do alvo (data-tour=...). Ausente = passo centralizado, sem spotlight. */
  target?: string;
  title: string;
  body: string;
  /** Navegação REAL executada ao ENTRAR no passo (ex.: abrir a Administração). */
  go?: () => void;
}

interface Props {
  steps: TourStep[];
  onFinish: () => void;
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/**
 * Tour de onboarding leve (sem lib externa): spotlight no elemento-alvo
 * (data-tour=...) + tooltip com Avançar/Pular. O tour NAVEGA DE VERDADE pelo
 * app: cada passo pode ter um `go()` que muda de tela ao entrar, e a medição
 * do alvo re-tenta por alguns segundos (a tela nova precisa renderizar).
 * Alvo inexistente vira passo centralizado - nunca trava o usuário.
 */
export function OnboardingTour({ steps, onFinish }: Props) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const step = steps[idx];

  // navegação real do passo - roda ao entrar (inclusive no primeiro)
  useEffect(() => {
    steps[idx]?.go?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx]);

  useLayoutEffect(() => {
    if (!step?.target) {
      setRect(null);
      return;
    }
    let tries = 0;
    let retry: ReturnType<typeof setTimeout> | null = null;
    const measure = () => {
      const el = document.querySelector(step.target!) as HTMLElement | null;
      if (!el) {
        setRect(null);
        // a tela do passo pode ainda estar montando (go() acabou de navegar)
        if (tries++ < 20) retry = setTimeout(measure, 150);
        return;
      }
      el.scrollIntoView({ block: "nearest" });
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      if (retry) clearTimeout(retry);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [step?.target, idx]);

  // Esc pula o tour
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onFinish();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onFinish]);

  if (!step) return null;

  const isLast = idx === steps.length - 1;
  const pad = 8;
  const highlight: React.CSSProperties | null = rect
    ? {
        position: "fixed",
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
        borderRadius: 10,
        boxShadow: "0 0 0 9999px rgba(10, 12, 18, 0.6)",
        border: "2px solid var(--accent, #c15f3c)",
        pointerEvents: "none",
        zIndex: 10000,
        transition: "all 0.2s ease",
      }
    : null;

  // tooltip: abaixo do alvo quando couber; senão centralizado
  const tipStyle: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: Math.min(rect.top + rect.height + pad + 8, window.innerHeight - 230),
        left: Math.min(Math.max(12, rect.left), window.innerWidth - 332),
        width: 320,
        zIndex: 10001,
      }
    : {
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: 340,
        zIndex: 10001,
      };

  return (
    <div role="dialog" aria-label="Tour de boas-vindas">
      {/* sem alvo: fundo escuro próprio (o spotlight usa box-shadow como cutout) */}
      {!rect && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,18,0.6)", zIndex: 10000 }} />
      )}
      {highlight && <div style={highlight} />}
      <div
        style={{
          ...tipStyle,
          background: "var(--bg-elevated, #fff)",
          color: "var(--text, #1f1e1d)",
          borderRadius: 12,
          padding: 16,
          boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
        }}
      >
        <div style={{ fontSize: "0.75rem", color: "var(--text-dim, #8a8f98)", marginBottom: 4 }}>
          Passo {idx + 1} de {steps.length}
        </div>
        <h3 style={{ margin: "0 0 6px", fontSize: "1.05rem" }}>{step.title}</h3>
        <p style={{ margin: "0 0 14px", fontSize: "0.9rem", lineHeight: 1.5 }}>{step.body}</p>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <button className="action-btn" type="button" onClick={onFinish} style={{ fontSize: "0.85rem" }}>
            Pular
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {idx > 0 && (
              <button className="btn-reject" type="button" onClick={() => setIdx((i) => i - 1)}>
                Voltar
              </button>
            )}
            <button
              className="btn-approve"
              type="button"
              onClick={() => (isLast ? onFinish() : setIdx((i) => i + 1))}
            >
              {isLast ? "Concluir" : "Avançar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Passos do tour do 1º login do admin - com NAVEGAÇÃO REAL: o tour leva a
 * pessoa pelas telas (chat -> Administração/Repositórios -> Usuários -> volta
 * pro chat), destacando o elemento certo em cada uma.
 */
export function buildAdminTourSteps(nav: { toChat: () => void; toAdminTab: (tab: string) => void }): TourStep[] {
  return [
    {
      target: '[data-tour="account"]',
      title: "Bem-vindo(a)!",
      body: "Este é o menu da sua conta: seu nome, sua foto e a entrada para a Administração. Vou te levar pelo caminho - é só avançar.",
      go: nav.toChat,
    },
    {
      target: '[data-tour="admin-nav-repos"]',
      title: "Conecte seus repositórios",
      body: "Esta é a Administração, aberta em Repositórios: conecte GitHub, GitLab ou Azure DevOps - é o que a IA lê para responder e documentar.",
      go: () => nav.toAdminTab("repos"),
    },
    {
      target: '[data-tour="admin-nav-users"]',
      title: "Convide sua equipe",
      body: "Em Usuários você aprova quem pediu acesso e convida as pessoas do seu time, definindo o papel de cada uma.",
      go: () => nav.toAdminTab("users"),
    },
    {
      target: '[data-tour="new-chat"]',
      title: "Tudo pronto",
      body: "De volta ao chat: comece uma nova conversa aqui e pergunte sobre o seu código, as regras de negócio ou peça ajuda de suporte.",
      go: nav.toChat,
    },
  ];
}
